import { useMemo } from "react";
import { Box, Typography } from "@mui/material";
import { AgGridReact } from "ag-grid-react";
import type { ColDef, ICellRendererParams } from "ag-grid-community";

import { CenteredLazyLoading } from "@/controls/LazyLoading";
import { useCampaignBillets } from "../hooks/useProductsHistory";
import {
	STAND_COLUMNS,
	type CampaignBilletRow,
	type ProductsHistoryRow,
	type StandColumnName,
} from "../models/ProductsHistory";

const CENTER_CELL = { textAlign: "center" } as const;
const CENTER_HEADER = "ag-cell-center-header";

const WrongPassCell = ({
	data,
	colDef,
}: ICellRendererParams<CampaignBilletRow>) => {
	const stand = data?.[colDef?.field as StandColumnName];
	if (!stand?.WrongPass) return null;
	return (
		<Box
			title={`Pass config: ${stand.PassNameConfigured ?? "-"}\nPass run: ${stand.PassNameSetup ?? "-"}`}
			sx={{
				width: 10,
				height: 10,
				borderRadius: "50%",
				bgcolor: "error.main",
				mx: "auto",
			}}
		/>
	);
};

const buildColumnDefs = (): ColDef<CampaignBilletRow>[] => [
	{
		field: "Start",
		headerName: "Start",
		width: 150,
		sortable: true,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
	},
	{
		field: "End",
		headerName: "End",
		width: 150,
		sortable: true,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
	},
	...STAND_COLUMNS.map<ColDef<CampaignBilletRow>>((standName) => ({
		field: standName,
		headerName: standName,
		width: 70,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
		cellRenderer: WrongPassCell,
	})),
];

interface Props {
	campaign: ProductsHistoryRow;
}

export const CampaignBilletsGrid = ({ campaign }: Props) => {
	const query = useCampaignBillets({
		campaignId: campaign.CampaignId,
	});
	const columnDefs = useMemo(buildColumnDefs, []);
	const rows = query.data ?? [];

	if (query.isLoading) return <CenteredLazyLoading />;

	if (rows.length === 0) {
		return (
			<Box
				sx={{
					display: "flex",
					flexDirection: "column",
					alignItems: "center",
					justifyContent: "center",
					height: "100%",
					gap: 0.5,
				}}
			>
				<Typography variant="subtitle1" color="text.secondary">
					No wrong pass billets in this campaign
				</Typography>
				<Typography variant="caption" color="text.secondary">
					{campaign.ProductName} — {campaign.FirstBilletStart ?? ""}
				</Typography>
			</Box>
		);
	}

	return (
		<AgGridReact<CampaignBilletRow>
			columnDefs={columnDefs}
			rowData={rows}
			rowModelType="clientSide"
			defaultColDef={{ resizable: true }}
			autoSizeStrategy={{ type: "fitGridWidth" }}
			suppressContextMenu
			suppressCellFocus
			headerHeight={32}
			rowHeight={28}
		/>
	);
};
